import { useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth/auth-context';
import { Tag } from '../components/Tag';
import {
  Campo,
  Carregando,
  ErroBox,
  Form,
  LinhaCampos,
  Modal,
  ModalRodape,
  Secao,
  Vazio,
} from '../components/ui';
import { limpar } from './Eventos';
import { ListaDeParticipantes } from './Participantes';

/**
 * Programação, palestrantes, objetivos, público-alvo e temas do evento.
 *
 * ⚠️ Patrocinadores saíram da tela em 16/10/2026, por decisão do Gabriel ("nesse
 * momento a gente ainda não quer colocar"). A API (`/events/:id/sponsors`) e a
 * tabela continuam; para voltar, a aba e o componente estão no histórico do git,
 * no commit que fez esta remoção. Um `?aba=patrocinadores` antigo cai na aba padrão.
 *
 * Abas, e não cinco itens de menu: são todos conteúdo DO evento, e quem está
 * montando um passa por todos em sequência. Cinco telas obrigariam a voltar ao
 * evento a cada troca.
 *
 * Deixou de ser uma PÁGINA e virou um bloco: mora dentro de `EventoDetalhe`,
 * abaixo dos dados. Ter um botão "Conteúdo" que levava a outro endereço fazia
 * parecer que eram duas coisas — e eram a mesma, partida ao meio por uma
 * navegação que não precisava existir.
 */

type Aba =
  | 'participantes'
  | 'programacao'
  | 'palestrantes'
  | 'objetivos'
  | 'publico'
  | 'temas';

// Participantes primeiro, e é a aba padrão: é a lista que a jornada usa, a que
// mais se consulta depois que o evento está montado, e a única que costuma
// mudar todo dia.
const ABAS: { id: Aba; rotulo: string; icone: string }[] = [
  { id: 'participantes', rotulo: 'Participantes', icone: 'ti-users' },
  { id: 'programacao', rotulo: 'Programação', icone: 'ti-calendar-time' },
  { id: 'palestrantes', rotulo: 'Palestrantes', icone: 'ti-microphone-2' },
  { id: 'objetivos', rotulo: 'Objetivos', icone: 'ti-target' },
  { id: 'publico', rotulo: 'Público-alvo', icone: 'ti-users-group' },
  { id: 'temas', rotulo: 'Temas', icone: 'ti-tags' },
];

interface Palestrante {
  id: string;
  name: string;
  role: string | null;
  company: string | null;
  bio: string | null;
  linkedin: string | null;
  expertise: string[] | null;
  activities: { activity: { id: string; title: string } }[];
}

interface Atividade {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  room: string | null;
  capacity: number | null;
  speakers: { speaker: { id: string; name: string; role: string | null } }[];
}

interface Objetivo {
  id: string;
  kind: string;
  content: string;
}

interface Tema {
  id: string;
  name: string;
  slug: string;
}

/**
 * O endereço antigo, `/eventos/:id/conteudo`, agora leva ao evento.
 *
 * Existe porque link não é só navegação de dentro do produto: alguém pode ter
 * a aba aberta, o endereço colado num chat ou salvo nos favoritos, e um 404 em
 * cima disso não explica que a tela apenas mudou de lugar. Leva o `?aba=`
 * junto, senão quem tinha o link de "Temas" cairia na Programação.
 */
export function RedirecionarConteudoDoEvento() {
  const { id } = useParams();
  const [busca] = useSearchParams();
  const query = busca.toString();

  return <Navigate to={`/eventos/${id}${query ? `?${query}` : ''}`} replace />;
}

export function ConteudoDoEvento() {
  const { id: eventId = '' } = useParams();
  const { pode } = useAuth();

  /**
   * A aba mora no endereço, não no estado do componente.
   *
   * Com `useState`, recarregar a página, voltar de uma tela e até chegar aqui
   * por um atalho de "Temas" caíam sempre na Programação — quem escolheu uma
   * aba era jogado de volta para a primeira. No endereço, ela sobrevive à
   * recarga e dá para mandar o link de uma aba específica para alguém.
   */
  const [busca, setBusca] = useSearchParams();
  const pedida = busca.get('aba') as Aba | null;
  const aba: Aba = ABAS.some((a) => a.id === pedida) ? pedida! : 'participantes';

  // `replace` para a troca de aba não empilhar uma entrada no histórico a cada
  // clique — voltar deve sair da tela, não desfazer cinco cliques de aba.
  const setAba = (nova: Aba) => setBusca({ aba: nova }, { replace: true });

  // Sem consulta ao evento aqui: quem renderiza este bloco já o carregou, e
  // pedir de novo só para escrever o nome num cabeçalho que não existe mais
  // seria uma requisição por nada.
  const podeEditar = pode('events.update');

  return (
    <>
      <div className="abas" style={{ marginTop: 16 }}>
        {ABAS.map((a) => (
          <button
            key={a.id}
            className={`aba${aba === a.id ? ' ativa' : ''}`}
            onClick={() => setAba(a.id)}
          >
            <i className={`ti ${a.icone}`} /> {a.rotulo}
          </button>
        ))}
      </div>

      {aba === 'participantes' && <ListaDeParticipantes />}
      {aba === 'programacao' && <Programacao eventId={eventId} podeEditar={podeEditar} />}
      {aba === 'palestrantes' && <Palestrantes eventId={eventId} podeEditar={podeEditar} />}
      {aba === 'objetivos' && <ObjetivosDoTipo eventId={eventId} podeEditar={podeEditar} tipo="objetivo" />}
      {aba === 'publico' && <ObjetivosDoTipo eventId={eventId} podeEditar={podeEditar} tipo="publico" />}
      {aba === 'temas' && <Temas eventId={eventId} podeEditar={podeEditar} />}
    </>
  );
}

/** Hook de CRUD compartilhado: as cinco abas fazem exatamente a mesma coisa. */
function useRecurso<T>(eventId: string, caminho: string, chave: string) {
  const cliente = useQueryClient();
  const base = `/events/${eventId}/${caminho}`;
  const queryKey = [chave, eventId];

  const consulta = useQuery({ queryKey, queryFn: () => api<T[]>(base) });
  const invalidar = () => {
    void cliente.invalidateQueries({ queryKey });
    void cliente.invalidateQueries({ queryKey: ['evento', eventId] });
  };

  const criar = useMutation({
    mutationFn: (corpo: unknown) => api(base, { method: 'POST', body: corpo }),
    onSuccess: invalidar,
  });

  const remover = useMutation({
    mutationFn: (id: string) => api(`${base}/${id}`, { method: 'DELETE' }),
    onSuccess: invalidar,
  });

  return { consulta, criar, remover };
}

function BotaoRemover({ aoRemover }: { aoRemover: () => void }) {
  return (
    <button className="icon-btn" title="Remover" onClick={aoRemover}>
      <i className="ti ti-trash" style={{ fontSize: 15 }} />
    </button>
  );
}

/* ─────────── Programação ─────────── */

function Programacao({ eventId, podeEditar }: { eventId: string; podeEditar: boolean }) {
  const { consulta, criar, remover } = useRecurso<Atividade>(eventId, 'activities', 'atividades');
  const palestrantes = useQuery({
    queryKey: ['palestrantes', eventId],
    queryFn: () => api<Palestrante[]>(`/events/${eventId}/speakers`),
  });
  const [criando, setCriando] = useState(false);

  if (consulta.isPending) return <Carregando />;
  if (consulta.isError) return <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />;

  return (
    <Secao
      titulo="Programação"
      descricao="A agenda alimenta as recomendações: é com ela que a IA sugere o que cada participante deveria assistir."
      acao={
        podeEditar && (
          <button className="btn btn-primary" onClick={() => setCriando(true)}>
            <i className="ti ti-plus" /> Nova atividade
          </button>
        )
      }
    >
      {consulta.data!.length === 0 ? (
        <Vazio icone="ti-calendar-time" titulo="Programação vazia" descricao="Adicione palestras, painéis, workshops e rodadas." />
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="tabela-envolvente">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Atividade</th>
                  <th>Quando</th>
                  <th>Onde</th>
                  <th>Palestrantes</th>
                  {podeEditar && <th />}
                </tr>
              </thead>
              <tbody>
                {consulta.data!.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <div className="celula-principal">{a.title}</div>
                      {a.category && <div className="celula-secundaria">{a.category}</div>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {a.date
                        ? new Date(a.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
                        : <span style={{ color: 'var(--text-dim)' }}>Sem data</span>}
                      {a.startTime && (
                        <div className="celula-secundaria">
                          {a.startTime}
                          {a.endTime && `–${a.endTime}`}
                        </div>
                      )}
                    </td>
                    <td>
                      {a.room || <span style={{ color: 'var(--text-dim)' }}>—</span>}
                      {a.capacity != null && (
                        <div className="celula-secundaria">{a.capacity} lugares</div>
                      )}
                    </td>
                    <td>
                      {a.speakers.length === 0 ? (
                        <span style={{ color: 'var(--text-dim)' }}>—</span>
                      ) : (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {a.speakers.map((s) => (
                            <Tag key={s.speaker.id}>{s.speaker.name}</Tag>
                          ))}
                        </div>
                      )}
                    </td>
                    {podeEditar && (
                      <td>
                        <div className="acoes-linha">
                          <BotaoRemover aoRemover={() => remover.mutate(a.id)} />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {criando && (
        <Modal titulo="Nova atividade" aoFechar={() => setCriando(false)} largura={600}>
          <Form
            aoEnviar={(d) => {
              const { speakerId, capacity, ...resto } = d;
              criar.mutate(
                {
                  ...limpar(resto),
                  ...(capacity ? { capacity: Number(capacity) } : {}),
                  ...(speakerId ? { speakerIds: [speakerId] } : {}),
                },
                { onSuccess: () => setCriando(false) },
              );
            }}
          >
            {criar.isError && <div className="form-erro">{(criar.error as Error).message}</div>}

            <Campo label="Título" name="title" obrigatorio autoFocus />
            <Campo label="Descrição" name="description" />

            <LinhaCampos>
              <Campo
                label="Categoria"
                name="category"
                opcional
                placeholder="palestra, painel, workshop…"
              />
              <Campo label="Data" name="date" tipo="date" />
            </LinhaCampos>

            <LinhaCampos>
              <Campo label="Início" name="startTime" tipo="time" />
              <Campo label="Término" name="endTime" tipo="time" />
            </LinhaCampos>

            <LinhaCampos>
              <Campo label="Sala" name="room" opcional />
              <Campo label="Capacidade" name="capacity" tipo="number" opcional />
            </LinhaCampos>

            <Campo
              label="Palestrante"
              name="speakerId"
              opcoes={[
                { valor: '', rotulo: 'Nenhum' },
                ...(palestrantes.data ?? []).map((p) => ({ valor: p.id, rotulo: p.name })),
              ]}
              ajuda="Cadastre palestrantes na aba ao lado para vinculá-los aqui."
            />

            <ModalRodape>
              <button type="button" className="btn btn-secondary" onClick={() => setCriando(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" type="submit" disabled={criar.isPending}>
                {criar.isPending ? 'Salvando…' : 'Adicionar'}
              </button>
            </ModalRodape>
          </Form>
        </Modal>
      )}
    </Secao>
  );
}

/* ─────────── Palestrantes ─────────── */

function Palestrantes({ eventId, podeEditar }: { eventId: string; podeEditar: boolean }) {
  const { consulta, criar, remover } = useRecurso<Palestrante>(eventId, 'speakers', 'palestrantes');
  const [criando, setCriando] = useState(false);

  if (consulta.isPending) return <Carregando />;
  if (consulta.isError) return <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />;

  return (
    <Secao
      titulo="Palestrantes"
      descricao="Quem fala no evento. A IA usa a área de atuação para sugerir sessões relevantes a cada participante."
      acao={
        podeEditar && (
          <button className="btn btn-primary" onClick={() => setCriando(true)}>
            <i className="ti ti-plus" /> Novo palestrante
          </button>
        )
      }
    >
      {consulta.data!.length === 0 ? (
        <Vazio icone="ti-microphone-2" titulo="Nenhum palestrante" />
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {consulta.data!.map((p) => (
            <div key={p.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {[p.role, p.company].filter(Boolean).join(' · ') || '—'}
                  </div>

                  {p.expertise && p.expertise.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 10 }}>
                      {p.expertise.map((e) => (
                        <Tag key={e} type="info">
                          {e}
                        </Tag>
                      ))}
                    </div>
                  )}

                  {p.activities.length > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 10 }}>
                      {p.activities.map((a) => a.activity.title).join(' · ')}
                    </div>
                  )}
                </div>

                {podeEditar && (
                  <div className="acoes-linha">
                    <BotaoRemover aoRemover={() => remover.mutate(p.id)} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {criando && (
        <Modal titulo="Novo palestrante" aoFechar={() => setCriando(false)} largura={560}>
          <Form
            aoEnviar={(d) => {
              const { expertise, ...resto } = d;
              criar.mutate(
                {
                  ...limpar(resto),
                  ...(expertise
                    ? { expertise: expertise.split(',').map((e) => e.trim()).filter(Boolean) }
                    : {}),
                },
                { onSuccess: () => setCriando(false) },
              );
            }}
          >
            {criar.isError && <div className="form-erro">{(criar.error as Error).message}</div>}

            <Campo label="Nome" name="name" obrigatorio autoFocus />
            <LinhaCampos>
              <Campo label="Cargo" name="role" opcional />
              <Campo label="Empresa" name="company" opcional />
            </LinhaCampos>
            <Campo label="Bio" name="bio" />
            <Campo label="LinkedIn" name="linkedin" placeholder="https://linkedin.com/in/…" />
            <Campo
              label="Áreas de atuação"
              name="expertise"
              placeholder="IA, Dados, Produto"
              ajuda="Separadas por vírgula."
            />

            <ModalRodape>
              <button type="button" className="btn btn-secondary" onClick={() => setCriando(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" type="submit" disabled={criar.isPending}>
                {criar.isPending ? 'Salvando…' : 'Adicionar'}
              </button>
            </ModalRodape>
          </Form>
        </Modal>
      )}
    </Secao>
  );
}

/* ─────────── Objetivos e público-alvo ─────────── */

/**
 * Os dois tipos de `EventObjective` que restam, cada um na sua aba.
 *
 * Eram uma aba só, com um seletor de tipo no formulário e uma etiqueta em cada
 * linha. Separados (16/10/2026, pedido do Gabriel), cada aba diz para que serve
 * o que se cadastra ali, e o tipo vem da aba — não há como errar a escolha.
 *
 * As duas abas leem a MESMA lista (`objectives`) e filtram pelo tipo: uma
 * consulta, um cache, e criar numa aba atualiza a outra sem esforço.
 */
const TIPOS_DA_ABA = {
  objetivo: {
    titulo: 'Objetivos do evento',
    descricao:
      'O que o evento quer alcançar. A IA lê para conduzir a conversa, e cada objetivo conta na aderência de quem participa.',
    botao: 'Novo objetivo',
    icone: 'ti-target',
    vazio: 'Nenhum objetivo definido',
    vazioDescricao: 'Sem objetivos, a IA conduz a conversa sem saber o que o evento quer alcançar.',
  },
  publico: {
    titulo: 'Público-alvo',
    descricao:
      'Quem o evento quer na sala. Cada descrição conta na aderência, conferida contra o que a pessoa conta de si, o cargo e a empresa.',
    botao: 'Novo público',
    icone: 'ti-users-group',
    vazio: 'Nenhum público definido',
    vazioDescricao: 'Sem público-alvo, a aderência mede só os objetivos e os temas.',
  },
} as const;

type TipoDaAba = keyof typeof TIPOS_DA_ABA;

function ObjetivosDoTipo({
  eventId,
  podeEditar,
  tipo,
}: {
  eventId: string;
  podeEditar: boolean;
  tipo: TipoDaAba;
}) {
  const { consulta, criar, remover } = useRecurso<Objetivo>(eventId, 'objectives', 'objetivos');
  const [criando, setCriando] = useState(false);
  const textos = TIPOS_DA_ABA[tipo];

  if (consulta.isPending) return <Carregando />;
  if (consulta.isError) return <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />;

  /**
   * Na aba de objetivos entram também as linhas de tipos aposentados
   * (`perfil_ideal`, `criterio_sucesso`, `resultado`): a IA já não as lê, e sem
   * esta exceção elas ficariam no banco sem aparecer em aba nenhuma — e sem ter
   * como apagar.
   */
  const linhas = consulta.data!.filter((o) =>
    tipo === 'objetivo' ? !(o.kind in TIPOS_DA_ABA) || o.kind === 'objetivo' : o.kind === tipo,
  );

  return (
    <Secao
      titulo={textos.titulo}
      descricao={textos.descricao}
      acao={
        podeEditar && (
          <button className="btn btn-primary" onClick={() => setCriando(true)}>
            <i className="ti ti-plus" /> {textos.botao}
          </button>
        )
      }
    >
      {linhas.length === 0 ? (
        <Vazio icone={textos.icone} titulo={textos.vazio} descricao={textos.vazioDescricao} />
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {linhas.map((o) => (
            <div key={o.id} className="card card-sm">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                <div style={{ minWidth: 0 }}>
                  {o.kind !== tipo && (
                    <Tag type="muted" title="Tipo que a plataforma não usa mais. Pode apagar.">
                      {o.kind} · não é mais usado
                    </Tag>
                  )}
                  <div style={{ fontSize: 13, marginTop: o.kind !== tipo ? 8 : 0, lineHeight: 1.5 }}>
                    {o.content}
                  </div>
                </div>
                {podeEditar && (
                  <div className="acoes-linha">
                    <BotaoRemover aoRemover={() => remover.mutate(o.id)} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {criando && (
        <Modal titulo={textos.botao} aoFechar={() => setCriando(false)}>
          <Form
            aoEnviar={(d) =>
              criar.mutate({ kind: tipo, content: d.content }, { onSuccess: () => setCriando(false) })
            }
          >
            {criar.isError && <div className="form-erro">{(criar.error as Error).message}</div>}

            <Campo label="Descrição" name="content" obrigatorio autoFocus linhas={3} />

            <ModalRodape>
              <button type="button" className="btn btn-secondary" onClick={() => setCriando(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" type="submit" disabled={criar.isPending}>
                {criar.isPending ? 'Salvando…' : 'Adicionar'}
              </button>
            </ModalRodape>
          </Form>
        </Modal>
      )}
    </Secao>
  );
}

/* ─────────── Temas ─────────── */

function Temas({ eventId, podeEditar }: { eventId: string; podeEditar: boolean }) {
  const { consulta, criar, remover } = useRecurso<Tema>(eventId, 'themes', 'temas');
  const [criando, setCriando] = useState(false);

  if (consulta.isPending) return <Carregando />;
  if (consulta.isError) return <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />;

  return (
    <Secao
      titulo="Temas"
      descricao="Os assuntos do evento. Cada tema conta na aderência de quem participa, e a IA os usa para conduzir a conversa."
      acao={
        podeEditar && (
          <button className="btn btn-primary" onClick={() => setCriando(true)}>
            <i className="ti ti-plus" /> Novo tema
          </button>
        )
      }
    >
      {consulta.data!.length === 0 ? (
        <Vazio icone="ti-tags" titulo="Nenhum tema" />
      ) : (
        <div className="card">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {consulta.data!.map((t) => (
              <span key={t.id} className="tema-chip">
                {t.name}
                {podeEditar && (
                  <button
                    className="tema-remover"
                    onClick={() => remover.mutate(t.id)}
                    aria-label={`Remover ${t.name}`}
                  >
                    <i className="ti ti-x" style={{ fontSize: 11 }} />
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {criando && (
        <Modal
          titulo="Novo tema"
          descricao="O identificador é gerado a partir do nome."
          aoFechar={() => setCriando(false)}
        >
          <Form
            aoEnviar={(d) =>
              criar.mutate({ name: d.name }, { onSuccess: () => setCriando(false) })
            }
          >
            {criar.isError && <div className="form-erro">{(criar.error as Error).message}</div>}

            <Campo label="Nome" name="name" obrigatorio autoFocus />

            <ModalRodape>
              <button type="button" className="btn btn-secondary" onClick={() => setCriando(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" type="submit" disabled={criar.isPending}>
                {criar.isPending ? 'Salvando…' : 'Adicionar'}
              </button>
            </ModalRodape>
          </Form>
        </Modal>
      )}
    </Secao>
  );
}
